import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, MessageSquare, Users, Target,
  BarChart3, HelpCircle, RefreshCw,
  Calendar, Activity,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import api       from '@/lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

// ─── helpers ──────────────────────────────────────────────────────────────────
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0  },
  transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1], delay },
});

const CHART_COLORS = {
  emerald: '#10b981',
  blue:    '#3b82f6',
  violet:  '#8b5cf6',
  amber:   '#f59e0b',
  rose:    '#f43f5e',
};

const PIE_COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b'];

// ─── accent map ───────────────────────────────────────────────────────────────
const accentMap = {
  emerald: { icon: 'text-emerald-600', bg: 'bg-emerald-50', num: 'text-emerald-700' },
  blue:    { icon: 'text-blue-600',    bg: 'bg-blue-50',    num: 'text-blue-700'    },
  violet:  { icon: 'text-violet-600',  bg: 'bg-violet-50',  num: 'text-violet-700'  },
};

// ─── empty state ──────────────────────────────────────────────────────────────
const EmptyChart = ({ icon: Icon, message }) => (
  <div className="h-[280px] flex items-center justify-center">
    <div className="text-center">
      <Icon className="w-12 h-12 mx-auto mb-3 text-gray-200" />
      <p className="text-sm text-gray-400">{message}</p>
    </div>
  </div>
);

// ─── tooltip style ────────────────────────────────────────────────────────────
const tooltipStyle = {
  backgroundColor: 'white',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  fontSize: '12px',
  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
};

// ─── main component ───────────────────────────────────────────────────────────
export default function Analytics() {
  const [loading,      setLoading]      = useState(true);
  const [period,       setPeriod]       = useState('7');
  const [stats,        setStats]        = useState({ total_conversations: 0, total_messages: 0, total_leads: 0 });
  const [msgsData,     setMsgsData]     = useState([]);
  const [leadsData,    setLeadsData]    = useState([]);
  const [convByStatus, setConvByStatus] = useState([]);
  const [msgsByType,   setMsgsByType]   = useState([]);
  const [trends,       setTrends]       = useState({ conversation_growth: 0, message_growth: 0, most_used_keywords: [] });
  const [quickStats,   setQuickStats]   = useState({ avg_response_time: null, bot_automation_rate: null });

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);

      const [statsRes, msgsRes, leadsRes] = await Promise.all([
        api.get('/analytics/stats'),
        api.get(`/analytics/messages-chart?period=${period}`),
        api.get(`/analytics/leads-chart?period=${period}`),
      ]);

      setStats(statsRes.data.stats || {});

      setMsgsData(
        (msgsRes.data.data || []).map(item => ({
          date:     format(new Date(item.date), 'MMM dd'),
          messages: item.count || 0,
        }))
      );

      setLeadsData(
        (leadsRes.data.data || []).map(item => ({
          date:  format(new Date(item.date), 'MMM dd'),
          leads: item.count || 0,
        }))
      );

      const trendsRes = await api.get(`/analytics/trends?period=${period}`);
      setTrends({
        conversation_growth: trendsRes.data.data?.trends?.conversation_growth?.growth_percentage || 0,
        message_growth:      trendsRes.data.data?.trends?.daily_average?.messages               || 0,
        most_used_keywords:  [],
      });

      const msgAnalytics = await api.get(`/analytics/messages?period=${period}`);
      setQuickStats({
        avg_response_time:  msgAnalytics.data.data?.metrics?.avg_response_time_minutes || null,
        bot_automation_rate: msgAnalytics.data.data?.metrics?.bot_automation_rate      || null,
      });

      const dashRes = await api.get(`/analytics/dashboard?period=${period}`);
      const dash    = dashRes.data.dashboard;

      setConvByStatus([
        { name: 'Active',   value: dash.conversations.by_status.active   || 0 },
        { name: 'Pending',  value: dash.conversations.by_status.pending  || 0 },
        { name: 'Resolved', value: dash.conversations.by_status.resolved || 0 },
        { name: 'Archived', value: dash.conversations.by_status.archived || 0 },
      ]);

      setMsgsByType([
        { name: 'Customer', value: dash.messages.by_sender.customer || 0 },
        { name: 'Bot',      value: dash.messages.by_sender.bot      || 0 },
        { name: 'Agent',    value: dash.messages.by_sender.agent    || 0 },
      ]);

    } catch {
      toast.error('Failed to load analytics. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  // ─── stat cards config ────────────────────────────────────────────────────
  const statCards = [
    {
      title:      'Total Conversations',
      value:      stats.total_conversations || 0,
      icon:       MessageSquare,
      accent:     'emerald',
      change:     `${trends.conversation_growth >= 0 ? '+' : ''}${trends.conversation_growth}%`,
      changeType: 'positive',
    },
    {
      title:      'Total Messages',
      value:      stats.total_messages || 0,
      icon:       BarChart3,
      accent:     'blue',
      change:     `${trends.message_growth >= 0 ? '+' : ''}${trends.message_growth} avg/day`,
      changeType: 'positive',
    },
    {
      title:      'Total Leads',
      value:      stats.total_leads || 0,
      icon:       Target,
      accent:     'violet',
      change:     stats.total_leads > 0 ? `${stats.total_leads} total` : '0 total',
      changeType: 'positive',
    },
  ];

  // ─── loading state ────────────────────────────────────────────────────────
  if (loading && !msgsData.length) return (
    <div className="h-[calc(100vh-140px)] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="relative w-14 h-14">
          <div className="absolute inset-0 rounded-full border-4 border-emerald-100" />
          <div className="absolute inset-0 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" />
        </div>
        <p className="text-sm text-gray-400 font-medium">Loading analytics…</p>
      </div>
    </div>
  );

  // ─── render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-8" data-testid="analytics-page">

      {/* Header */}
      <motion.div {...fadeUp(0)} className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Analytics</h1>
          <p className="mt-1 text-sm text-gray-400">Track your WhatsApp automation performance.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40 h-9 rounded-xl border-gray-200 focus:border-emerald-400 text-sm">
              <Calendar className="w-3.5 h-3.5 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <button
            onClick={fetchAnalytics}
            disabled={loading}
            className="p-2 rounded-xl border border-gray-200 bg-white text-gray-500 hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50 transition-all disabled:opacity-50"
            aria-label="Refresh analytics"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </motion.div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {statCards.map((s, i) => {
          const ac = accentMap[s.accent];
          return (
            <motion.div key={s.title} {...fadeUp(0.05 + i * 0.06)}>
              <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{s.title}</span>
                  <div className={`w-9 h-9 rounded-xl ${ac.bg} flex items-center justify-center`}>
                    <s.icon className={`w-4 h-4 ${ac.icon}`} />
                  </div>
                </div>
                <div className={`text-4xl font-bold ${ac.num} mb-1 tabular-nums`}>{s.value}</div>
                <div className="flex items-center gap-1">
                  <TrendingUp className={`w-3 h-3 ${s.changeType === 'positive' ? 'text-emerald-500' : 'text-rose-500'}`} />
                  <span className={`text-xs font-semibold ${s.changeType === 'positive' ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {s.change}
                  </span>
                  <span className="text-xs text-gray-400">from last period</span>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Time Series Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Messages chart */}
        <motion.div {...fadeUp(0.23)}>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900">Messages Over Time</h2>
              <MessageSquare className="w-4 h-4 text-gray-400" />
            </div>
            <div className="p-6">
              {msgsData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={msgsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="date" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: '#10b981', strokeWidth: 1, strokeDasharray: '5 5' }} />
                    <Line
                      type="monotone" dataKey="messages"
                      stroke={CHART_COLORS.emerald} strokeWidth={3}
                      dot={{ fill: CHART_COLORS.emerald, r: 4, strokeWidth: 2, stroke: 'white' }}
                      activeDot={{ r: 6, strokeWidth: 2, stroke: 'white' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart icon={BarChart3} message="No message data available yet" />
              )}
            </div>
          </div>
        </motion.div>

        {/* Leads chart */}
        <motion.div {...fadeUp(0.28)}>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900">Leads Over Time</h2>
              <Target className="w-4 h-4 text-gray-400" />
            </div>
            <div className="p-6">
              {leadsData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={leadsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="date" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(16, 185, 129, 0.1)' }} />
                    <Bar dataKey="leads" fill={CHART_COLORS.emerald} radius={[8, 8, 0, 0]} maxBarSize={50} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart icon={Target} message="No leads data available yet" />
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Pie Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Conversations by status */}
        <motion.div {...fadeUp(0.33)}>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900">Conversations by Status</h2>
              <Users className="w-4 h-4 text-gray-400" />
            </div>
            <div className="p-6">
              {convByStatus.some(c => c.value > 0) ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={convByStatus} cx="50%" cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={90} dataKey="value"
                    >
                      {convByStatus.map((_, i) => (
                        <Cell key={`cell-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart icon={Users} message="No conversation data available yet" />
              )}
            </div>
          </div>
        </motion.div>

        {/* Messages by type */}
        <motion.div {...fadeUp(0.38)}>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900">Messages by Sender</h2>
              <MessageSquare className="w-4 h-4 text-gray-400" />
            </div>
            <div className="p-6">
              {msgsByType.some(m => m.value > 0) ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={msgsByType} cx="50%" cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={90} dataKey="value"
                    >
                      {msgsByType.map((_, i) => (
                        <Cell key={`cell-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart icon={MessageSquare} message="No message type data available yet" />
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

        {/* Quick Stats */}
        <motion.div {...fadeUp(0.43)}>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Activity className="w-4 h-4 text-emerald-600" />
              </div>
              <h2 className="text-base font-bold text-gray-900">Quick Stats</h2>
            </div>
            <div className="p-6 space-y-3">
              {[
                {
                  label: 'Avg Response Time',
                  value: quickStats.avg_response_time ? `${quickStats.avg_response_time} min` : 'N/A',
                  color: 'text-gray-900',
                },
                {
                  label: 'Bot Automation Rate',
                  value: quickStats.bot_automation_rate ? `${quickStats.bot_automation_rate}%` : 'N/A',
                  color: 'text-emerald-600',
                },
              ].map(({ label, value, color }) => (
                <div key={label} className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{label}</span>
                  <span className={`font-bold ${color}`}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Growth Metrics */}
        <motion.div {...fadeUp(0.48)}>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
              </div>
              <h2 className="text-base font-bold text-gray-900">Summary</h2>
            </div>
            <div className="p-6 space-y-3">
              {[
                { label: 'Total Conversations', value: stats.total_conversations || '0' },
                { label: 'Total Leads',         value: stats.total_leads         || '0' },
                { label: 'Total Messages',      value: stats.total_messages      || '0' },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{label}</span>
                  <span className="font-bold text-emerald-600">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Top Keywords */}
        <motion.div {...fadeUp(0.53)}>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center">
                <HelpCircle className="w-4 h-4 text-emerald-600" />
              </div>
              <h2 className="text-base font-bold text-gray-900">Top Keywords</h2>
            </div>
            <div className="p-6 space-y-3">
              {trends.most_used_keywords?.length > 0 ? (
                trends.most_used_keywords.slice(0, 3).map(k => (
                  <div key={k.keyword} className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 truncate">{k.keyword}</span>
                    <span className="font-bold text-gray-900">{k.usage_count}</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">No keyword data yet</p>
              )}
            </div>
          </div>
        </motion.div>
      </div>

    </div>
  );
}
